if (!customElements.get("byte-online-brief-cta")) {
  class ByteOnlineBriefCta extends HTMLElement {
    static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    static ALLOWED_EXTENSIONS = ["pdf", "ai", "psd", "jpg", "jpeg", "png"];

    constructor() {
      super();

      this.isOpen = false;
      this.isAdvancedVisible = false;
      this.isUploading = false;
      this.basicInputs = [];
      this.formPanel = null;
      this.advancedWrapper = null;
      this.openTrigger = null;
      this.firstFormInput = null;
      this.submitButton = null;

      this.uploadRoot = null;
      this.uploadInput = null;
      this.uploadUrlInput = null;
      this.uploadHint = null;
      this.uploadProgress = null;
      this.uploadBar = null;
      this.uploadStatus = null;
      this.uploadEndpoint = null;
      this.uploadMaxSize = 104857600;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleBasicInput = this.handleBasicInput.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleFileChange = this.handleFileChange.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#evaluateBasicStage();

      if (this.#hasFormErrors() || this.#wasFormPosted()) {
        this.open({ focus: false, animate: false });
      }
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const trigger = event.target.closest("[data-brief-action]");
      if (!trigger) return;
      if (!this.contains(trigger)) return;

      const briefAction = trigger.dataset.briefAction;

      if (briefAction === "open") {
        event.preventDefault();
        this.openTrigger = trigger;
        this.open();
      } else if (briefAction === "close") {
        event.preventDefault();
        this.close();
      }
    }

    handleBasicInput() {
      this.#evaluateBasicStage();
    }

    handleKeydown(event) {
      if (event.key !== "Escape") return;
      if (!this.isOpen) return;

      this.close();
    }

    async handleFileChange() {
      const selectedFile = this.uploadInput.files && this.uploadInput.files[0];

      if (!selectedFile) return;

      const validationError = this.#validateFile(selectedFile);

      if (validationError) {
        this.#renderUploadError(validationError);
        this.uploadInput.value = "";
        return;
      }

      await this.#uploadFile(selectedFile);
    }

    handleFormSubmit(event) {
      if (!this.isUploading) return;

      event.preventDefault();
    }

    open({ focus = true, animate = true } = {}) {
      if (this.isOpen) return;

      this.isOpen = true;
      this.dataset.briefState = "open";

      const formPanel = this.formPanel;
      if (formPanel) {
        formPanel.removeAttribute("inert");
        formPanel.setAttribute("aria-hidden", "false");
      }

      const openTrigger = this.querySelector('[data-brief-action="open"]');
      if (openTrigger) {
        openTrigger.setAttribute("aria-expanded", "true");
      }

      const activate = () => {
        this.classList.add("is-open-active");
      };

      if (animate) {
        requestAnimationFrame(() => {
          requestAnimationFrame(activate);
        });
      } else {
        activate();
      }

      if (focus && this.firstFormInput) {
        const focusTarget = this.firstFormInput;
        window.setTimeout(
          () => {
            focusTarget.focus({ preventScroll: false });
          },
          animate ? 350 : 0,
        );
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.dataset.briefState = "closed";
      this.classList.remove("is-open-active");

      const formPanel = this.formPanel;
      if (formPanel) {
        formPanel.setAttribute("inert", "");
        formPanel.setAttribute("aria-hidden", "true");
      }

      const openTrigger = this.querySelector('[data-brief-action="open"]');
      if (openTrigger) {
        openTrigger.setAttribute("aria-expanded", "false");
      }

      if (this.openTrigger && typeof this.openTrigger.focus === "function") {
        this.openTrigger.focus({ preventScroll: false });
      }
    }

    #cacheElements() {
      this.formPanel = this.querySelector("[data-brief-form-panel]");
      this.advancedWrapper = this.querySelector("[data-brief-stage-wrapper]");
      this.submitButton = this.querySelector(
        ".byte-online-brief-cta__form-submit",
      );

      const basicStage = this.querySelector('[data-brief-stage="basic"]');
      this.basicInputs = basicStage
        ? Array.from(basicStage.querySelectorAll("[data-brief-required]"))
        : [];

      this.firstFormInput = this.basicInputs[0] || null;

      this.uploadRoot = this.querySelector("[data-brief-upload]");

      if (this.uploadRoot) {
        this.uploadInput = this.uploadRoot.querySelector(
          "[data-brief-upload-input]",
        );
        this.uploadUrlInput = this.uploadRoot.querySelector(
          "[data-brief-upload-url]",
        );
        this.uploadHint = this.uploadRoot.querySelector(
          "[data-brief-upload-hint]",
        );
        this.uploadProgress = this.uploadRoot.querySelector(
          "[data-brief-upload-progress]",
        );
        this.uploadBar = this.uploadRoot.querySelector(
          "[data-brief-upload-bar]",
        );
        this.uploadStatus = this.uploadRoot.querySelector(
          "[data-brief-upload-status]",
        );
        this.uploadEndpoint = this.uploadRoot.dataset.uploadEndpoint || "";

        const parsedMaxSize = Number(this.uploadRoot.dataset.uploadMaxSize);
        if (Number.isFinite(parsedMaxSize) && parsedMaxSize > 0) {
          this.uploadMaxSize = parsedMaxSize;
        }
      }
    }

    #bindEvents() {
      this.addEventListener("click", this.boundHandleClick);
      document.addEventListener("keydown", this.boundHandleKeydown);

      for (const input of this.basicInputs) {
        input.addEventListener("input", this.boundHandleBasicInput);
        input.addEventListener("change", this.boundHandleBasicInput);
        input.addEventListener("blur", this.boundHandleBasicInput);
      }

      if (this.uploadInput) {
        this.uploadInput.addEventListener("change", this.boundHandleFileChange);
      }

      const form = this.querySelector(".byte-online-brief-cta__form");
      if (form) {
        form.addEventListener("submit", this.boundHandleFormSubmit);
      }
    }

    #unbindEvents() {
      this.removeEventListener("click", this.boundHandleClick);
      document.removeEventListener("keydown", this.boundHandleKeydown);

      for (const input of this.basicInputs) {
        input.removeEventListener("input", this.boundHandleBasicInput);
        input.removeEventListener("change", this.boundHandleBasicInput);
        input.removeEventListener("blur", this.boundHandleBasicInput);
      }

      if (this.uploadInput) {
        this.uploadInput.removeEventListener(
          "change",
          this.boundHandleFileChange,
        );
      }

      const form = this.querySelector(".byte-online-brief-cta__form");
      if (form) {
        form.removeEventListener("submit", this.boundHandleFormSubmit);
      }
    }

    #evaluateBasicStage() {
      if (this.basicInputs.length === 0) return;

      const areAllValid = this.basicInputs.every((input) =>
        this.#isInputValid(input),
      );
      this.#setAdvancedVisible(areAllValid);
    }

    #isInputValid(input) {
      const value = (input.value || "").trim();

      if (value.length === 0) return false;
      if (input.type === "email") {
        return ByteOnlineBriefCta.EMAIL_PATTERN.test(value);
      }

      return true;
    }

    #setAdvancedVisible(isVisible) {
      if (!this.advancedWrapper) return;
      if (isVisible === this.isAdvancedVisible) return;

      this.isAdvancedVisible = isVisible;
      this.advancedWrapper.classList.toggle("is-visible", isVisible);
      this.advancedWrapper.setAttribute("aria-hidden", String(!isVisible));

      if (isVisible) {
        this.advancedWrapper.removeAttribute("inert");
      } else {
        this.advancedWrapper.setAttribute("inert", "");
      }
    }

    #hasFormErrors() {
      return Boolean(
        this.querySelector(".byte-online-brief-cta__notice--error"),
      );
    }

    #wasFormPosted() {
      return Boolean(
        this.querySelector(".byte-online-brief-cta__notice--success"),
      );
    }

    #validateFile(file) {
      const extension = file.name.split(".").pop().toLowerCase();
      const isAllowedExtension =
        ByteOnlineBriefCta.ALLOWED_EXTENSIONS.includes(extension);

      if (!isAllowedExtension) {
        return "Unsupported file type. Please upload PDF, AI, PSD, JPG, or PNG.";
      }

      if (file.size > this.uploadMaxSize) {
        return "File is too large. Maximum upload size is 100MB.";
      }

      return null;
    }

    async #uploadFile(file) {
      if (!this.uploadEndpoint) {
        this.#renderUploadError("Upload service is not configured.");
        return;
      }

      try {
        this.#setUploadingState(true);
        this.#renderUploadProgress(0);

        const uploadResult = await this.#sendFileToWorker(file);

        if (this.uploadUrlInput) {
          this.uploadUrlInput.value = uploadResult.url;
        }

        this.#renderUploadSuccess(file.name);
      } catch (error) {
        this.#renderUploadError(
          "File upload failed. Please try again.",
        );
        if (this.uploadUrlInput) {
          this.uploadUrlInput.value = "";
        }
        console.error(error);
      } finally {
        this.#setUploadingState(false);
      }
    }

    #sendFileToWorker(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("file", file);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", this.uploadEndpoint);

        xhr.upload.addEventListener("progress", (progressEvent) => {
          if (!progressEvent.lengthComputable) return;

          const percentComplete = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100,
          );
          this.#renderUploadProgress(percentComplete);
        });

        xhr.addEventListener("load", () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new Error(`Upload failed: ${xhr.status}`));
            return;
          }

          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (parseError) {
            reject(parseError);
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload network error"));
        });

        xhr.send(formData);
      });
    }

    #setUploadingState(isUploading) {
      this.isUploading = isUploading;

      if (this.uploadRoot) {
        this.uploadRoot.classList.toggle("is-uploading", isUploading);
      }

      if (this.uploadInput) {
        this.uploadInput.disabled = isUploading;
      }

      if (this.submitButton) {
        this.submitButton.disabled = isUploading;
      }
    }

    #renderUploadProgress(percentComplete) {
      if (!this.uploadProgress || !this.uploadBar) return;

      this.uploadProgress.setAttribute("aria-hidden", "false");
      this.uploadProgress.classList.add("is-visible");
      this.uploadBar.style.width = `${percentComplete}%`;

      if (this.uploadStatus) {
        this.uploadStatus.textContent = `Uploading: ${percentComplete}%`;
        this.uploadStatus.classList.remove("has-error");
      }
    }

    #renderUploadSuccess(fileName) {
      if (this.uploadProgress) {
        this.uploadProgress.classList.remove("is-visible");
        this.uploadProgress.setAttribute("aria-hidden", "true");
      }

      if (this.uploadStatus) {
        this.uploadStatus.textContent = `Uploaded: ${fileName}`;
        this.uploadStatus.classList.remove("has-error");
        this.uploadStatus.classList.add("is-success");
      }
    }

    #renderUploadError(message) {
      if (this.uploadProgress) {
        this.uploadProgress.classList.remove("is-visible");
        this.uploadProgress.setAttribute("aria-hidden", "true");
      }

      if (this.uploadStatus) {
        this.uploadStatus.textContent = message;
        this.uploadStatus.classList.remove("is-success");
        this.uploadStatus.classList.add("has-error");
      }
    }
  }

  customElements.define("byte-online-brief-cta", ByteOnlineBriefCta);
}